import { useQuery } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useState } from 'react';
import { buscarVoucherPorCodigo, type Voucher } from '../api/vouchers';
import { DrawerEntrega } from '../components/DrawerEntrega';
import { CabecalhoTela } from '../components/Shell';
import { BlocoAviso, Botao, Campo, Chip } from '../components/ui';
import { formatarData } from './Clientes';

export function VouchersPagina(): ReactNode {
  const [entrada, setEntrada] = useState('');
  const [termo, setTermo] = useState<string | null>(null);
  const [entregaAberta, setEntregaAberta] = useState(false);

  const q = useQuery({
    queryKey: ['voucher', 'busca', termo],
    queryFn: () => buscarVoucherPorCodigo(termo ?? ''),
    enabled: termo !== null,
  });

  const buscar = (e: FormEvent): void => {
    e.preventDefault();
    setTermo(entrada.trim().toUpperCase().replace(/\s+/g, ''));
  };

  const v: Voucher | null | undefined = q.data;

  return (
    <div>
      <CabecalhoTela titulo="Buscar voucher" />
      <form onSubmit={buscar} className="flex max-w-form items-end gap-2">
        <Campo
          rotulo="Código do voucher"
          className="flex-1 font-codigo"
          placeholder="Código do voucher (ex.: VC-7KP2)"
          autoFocus
          value={entrada}
          onChange={(e) => setEntrada(e.target.value.toUpperCase())}
        />
        <Botao type="submit" carregando={q.isFetching}>
          Buscar
        </Botao>
      </form>

      <div className="mt-5 max-w-form">
        {q.isError && (
          <BlocoAviso tom="aviso" aoTentarDeNovo={() => void q.refetch()}>
            Não foi possível buscar agora.
          </BlocoAviso>
        )}
        {termo !== null && !q.isFetching && v === null && (
          <BlocoAviso tom="aviso">Nenhum voucher com esse código. Confira as letras.</BlocoAviso>
        )}
        {v && (
          <div className="rounded-2 border border-linha bg-superficie-alta p-4">
            <h2 className="font-display text-bloco-titulo font-bold uppercase text-tinta-forte">
              {v.descricao}
            </h2>
            <dl className="mt-3 grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1 text-corpo">
              <dt className="text-tinta-2">Dono</dt>
              <dd className="text-tinta-forte">{v.clienteNome}</dd>
              <dt className="text-tinta-2">Origem</dt>
              <dd className="text-tinta-forte">
                {v.papel === 'indicador'
                  ? `Nível ${v.nivel ?? '?'}, pela indicação`
                  : 'Brinde de boas-vindas'}
              </dd>
              <dt className="text-tinta-2">Código</dt>
              <dd className="font-codigo">{v.voucherCodigo}</dd>
            </dl>

            <div className="mt-4 flex items-center gap-3">
              {v.status === 'disponivel' ? (
                <>
                  <Chip tom="acento">Disponível</Chip>
                  <Botao onClick={() => setEntregaAberta(true)}>Registrar entrega</Botao>
                </>
              ) : (
                <div>
                  <Chip tom="apagado">Entregue</Chip>
                  <p className="mt-2 text-corpo text-tinta-2">
                    Este voucher já foi retirado
                    {v.entregueEm ? ` em ${formatarData(v.entregueEm)}` : ''}
                    {v.retiradoPor ? ` por ${v.retiradoPor}` : ''}. Não pode ser retirado de novo.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {v && (
        <DrawerEntrega
          aberto={entregaAberta}
          voucherId={v.id}
          brinde={v.descricao}
          aoFechar={() => setEntregaAberta(false)}
          aoConcluir={() => void q.refetch()}
        />
      )}
    </div>
  );
}
